import React, { useState } from 'react';
import { Pressable, ScrollView, StyleSheet, View } from 'react-native';
import PageHeader from '../../components/account/PageHeader';
import Button from '../../components/ui/Button';
import Icon, { IconName } from '../../components/ui/Icon';
import Text from '../../components/ui/Text';
import {
  colors,
  hairline,
  radii,
  screenPadding,
  spacing,
  useResponsive,
} from '../../theme';

/**
 * Privacy & security.
 *
 * ---------------------------------------------------------------------------
 * TWO THINGS, BOTH REAL
 * ---------------------------------------------------------------------------
 * This page used to carry four switches and three more rows - app lock, live
 * location, personalised offers, analytics, phone change, device list, data
 * export - every one of them waiting on a system that does not exist yet. The
 * switches saved a preference nothing honoured and the rows were dead taps.
 * All of it is gone, on request: a privacy page must not advertise controls
 * it cannot honour.
 *
 * What is left is exactly what works end to end:
 *
 *   - Account deletion - guideline 5.1.1(v). The terms unfold on the row, the
 *     confirmation and the `DELETE /auth/me` call live in `HomeScreen`.
 *   - The policy, written out in full rather than linked. A link to a page
 *     that does not exist yet is worse than the text.
 *
 * When one of the removed controls gains a real backend, its row comes back
 * from git history - restoring one is a paste, not a rebuild.
 */

/** The plain-language policy, in the order people ask about it. */
const POLICY: Array<{ id: string; title: string; body: string; icon: IconName }> =
  [
    {
      id: 'p1',
      title: 'What we collect',
      icon: 'user',
      body:
        'Your phone number, because it is how you sign in. Your addresses and bookings, because the work has to reach a door. Your location while you are choosing an address, and only then. Nothing else - no contacts, no photos, no browsing outside the app.',
    },
    {
      id: 'p2',
      title: 'Who can see it',
      icon: 'users',
      body:
        'The professional assigned to a booking sees your first name, the address and the service - never your number, which is why chat exists. Payment details go to the gateway and are never stored by Homingo: only the card brand and its last four digits reach this app.',
    },
    {
      id: 'p3',
      title: 'How long it is kept',
      icon: 'clock',
      body:
        /*
         * The deletion sentence says what STOPS, not what is kept.
         *
         * It has been three things. `meetroj` wrote "removes everything except
         * records tax law requires", which was true of the hard delete that
         * existed then. Soft delete made it false, so it became "closes it
         * rather than erasing it… everything on it is held" - true, and too
         * much retention to put in front of somebody who has just decided to
         * leave.
         *
         * This is the third: accurate, and about cessation rather than
         * storage. It does not claim erasure, which it must not - the account
         * is closed and kept, `/legal/delete-account` says so in full, and a
         * policy in the app that contradicted the published page would be
         * worse than either alone.
         */
        'Bookings are kept while your account exists, because they are your receipts. Chat messages are kept for 90 days after a job finishes. Deleting your account ends your access and we stop using your information.',
    },
    {
      id: 'p4',
      title: 'Security',
      icon: 'shieldCheck',
      body:
        'Everything travels over HTTPS. Sign-in is by one-time code, so there is no password to leak. On this device, your session is stored unencrypted for now - it will move to the iOS Keychain and Android Keystore before release.',
    },
  ];

/**
 * What deletion means, spelled out BEFORE the button - the terms somebody
 * agrees to by pressing it, not a surprise read afterwards.
 *
 * ---------------------------------------------------------------------------
 * THREE LINES, AND WHAT WAS CUT
 * ---------------------------------------------------------------------------
 * There were six. Two of them said what the account still holds - "nothing is
 * erased", the profile and addresses and bookings that stay - and those are
 * gone at Vishal's request, on the same reasoning as the deleted-account
 * screen: a settings page is not the place to recite a retention policy at
 * somebody who has just decided to leave.
 *
 * What is left is the three things that change what they do next: the tap does
 * not delete anything yet, bookings still go ahead, and the account can come
 * back. Nothing here describes what is kept to make that last one possible.
 *
 * `/legal/delete-account` still states the retention in full, and must -
 * leaving it off a screen is a choice about emphasis; taking it off the
 * published page would be a different thing entirely.
 */
const DELETE_TERMS: string[] = [
  'This asks us to delete the account. Our team acts on it, or it goes through on its own after 90 days.',
  'Until then nothing changes - any booking you have will still go ahead as normal.',
  'You can bring the account back at any time by signing in with this same number.',
];

type Props = {
  onBack: () => void;
  /** Opens the deletion confirmation - see guideline 5.1.1(v) in `services/auth`. */
  onDeleteAccount: () => void;
};

function Row({
  icon,
  title,
  children,
  tone = 'plain',
}: {
  icon: IconName;
  title: string;
  children?: React.ReactNode;
  tone?: 'plain' | 'danger';
}) {
  const tint = tone === 'danger' ? colors.danger : colors.primary;
  return (
    <>
      <View
        style={[
          styles.mark,
          tone === 'danger' ? styles.markDanger : null,
        ]}
      >
        <Icon name={icon} size={17} color={tint} />
      </View>

      <View style={styles.body}>
        <Text
          variant="subtitle"
          color={tone === 'danger' ? colors.danger : undefined}
        >
          {title}
        </Text>
        {children}
      </View>
    </>
  );
}

function PrivacyScreen({ onBack, onDeleteAccount }: Props) {
  const resp = useResponsive();
  /** Which policy section is expanded, or null. One at a time. */
  const [open, setOpen] = useState<string | null>(null);
  /** Whether the delete row's terms panel is unfolded. */
  const [deleteOpen, setDeleteOpen] = useState(false);

  return (
    <View style={styles.root}>
      <PageHeader title="Privacy & Security" onBack={onBack} />

      {/*
        `flex: 1` on the scroller itself, not only on its content.

        Inside a flex column a ScrollView with no flex of its own sizes to its
        content and overflows the parent, so the foot of a long list is simply
        cut off. Flexed, it takes the space left under the header and scrolls
        inside it.
      */}
      <ScrollView
        style={styles.scroller}
        /* Zero on a phone; on a tablet it holds the page to a readable
           column and centres it. See `theme/responsive.ts`. */
        contentContainerStyle={[styles.page, resp.page]}
        showsVerticalScrollIndicator={false}
      >
        {/* ------------------------------------------------------ your account */}
        <Text variant="labelSm" color={colors.inkMuted}>
          Your account
        </Text>

        <View style={styles.card}>
          {/*
            Deletion is the only control on the page, and it is deliberately
            not a bare button. The row unfolds the full terms first - what
            happens, when, and how to undo it - and only the button inside the
            panel raises the confirm. Nobody deletes an account off a single
            tap on a list row.
          */}
          <Pressable
            accessibilityRole="button"
            accessibilityState={{ expanded: deleteOpen }}
            accessibilityLabel="Delete my account"
            onPress={() => setDeleteOpen(current => !current)}
            style={({ pressed }) => [
              styles.row,
              pressed ? styles.pressed : null,
            ]}
          >
            <Row icon="trash" title="Delete my account" tone="danger">
              {/* No longer says "nothing is erased" - see `DELETE_TERMS`. */}
              <Text variant="caption" color={colors.inkMuted}>
                Closes your account and signs you out everywhere. You can bring
                it back with the same number.
              </Text>
            </Row>
            <Icon
              name={deleteOpen ? 'chevronDown' : 'chevronRight'}
              size={15}
              color={colors.borderStrong}
            />
          </Pressable>

          {deleteOpen ? (
            <View style={styles.deletePanel}>
              {DELETE_TERMS.map(line => (
                <Text key={line} variant="caption" color={colors.inkMuted}>
                  {'•  '}
                  {line}
                </Text>
              ))}
              <Button
                label="Delete my account"
                variant="danger"
                leadingIcon="trash"
                /* HomeScreen's handler gates guests and raises the real
                   confirmation - the one that calls DELETE /auth/me. */
                onPress={onDeleteAccount}
                style={styles.deleteButton}
              />
            </View>
          ) : null}
        </View>

        {/* -------------------------------------------------------- the policy */}
        <Text variant="labelSm" color={colors.inkMuted}>
          Our policy
        </Text>

        <View style={styles.card}>
          {POLICY.map((section, i) => {
            const expanded = open === section.id;
            return (
              <View key={section.id} style={i > 0 ? styles.divided : null}>
                <Pressable
                  accessibilityRole="button"
                  accessibilityState={{ expanded }}
                  accessibilityLabel={section.title}
                  onPress={() => setOpen(expanded ? null : section.id)}
                  style={({ pressed }) => [
                    styles.row,
                    pressed ? styles.pressed : null,
                  ]}
                >
                  <Row icon={section.icon} title={section.title} />
                  <Icon
                    name={expanded ? 'chevronDown' : 'chevronRight'}
                    size={15}
                    color={colors.borderStrong}
                  />
                </Pressable>

                {expanded ? (
                  <Text
                    variant="caption"
                    color={colors.inkMuted}
                    style={styles.answer}
                  >
                    {section.body}
                  </Text>
                ) : null}
              </View>
            );
          })}
        </View>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: colors.background,
  },
  scroller: {
    flex: 1,
  },
  page: {
    /* Vertical only: the horizontal gutter comes from `resp.page` on the
       contentContainer, which also carries the tablet inset. `padding`
       here was silently overridden by it. */
    paddingVertical: screenPadding,
    gap: spacing.sm,
    // Clears the floating tab bar, which overlays the bottom of the screen.
    paddingBottom: 120,
  },
  card: {
    borderRadius: radii.lg,
    backgroundColor: colors.surface,
    marginBottom: spacing.md,
    ...hairline,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    padding: spacing.md,
  },
  /* On the child rather than the card, so the first row has no rule above it. */
  divided: {
    borderTopWidth: hairline.borderWidth,
    borderTopColor: hairline.borderColor,
  },
  mark: {
    width: 34,
    height: 34,
    borderRadius: radii.sm,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.surfaceAccent,
  },
  markDanger: {
    backgroundColor: colors.dangerSurface,
  },
  body: {
    flex: 1,
    gap: 2,
  },
  answer: {
    paddingHorizontal: spacing.md,
    paddingBottom: spacing.md,
    // Lines up under the title, clear of its mark.
    paddingLeft: spacing.md + 34 + spacing.md,
  },
  deletePanel: {
    paddingHorizontal: spacing.md,
    paddingBottom: spacing.md,
    // Same alignment as `answer`: under the row's title, clear of its mark.
    paddingLeft: spacing.md + 34 + spacing.md,
    gap: spacing.xs,
  },
  deleteButton: {
    marginTop: spacing.sm,
  },
  pressed: {
    opacity: 0.6,
  },
});

export default PrivacyScreen;
